import React, { useState, useEffect } from "react";
import { Button, ButtonGroup, Card, Col, Container, Placeholder, Row, Spinner, Table } from "reactstrap";
import { useAuthWithRoute, useDataWithRoute, useTitleFromComponent, useUrlState } from "../../../utils/hooks";
import { VsiBubble, VsiH4, VsiH6, VsiSpanText } from "../../../styled/App.styled";
import { useVsiAppContext } from "../../../stores/VsiApp";
import TreeMap from "../../../components/TreeMap";
import themeStyles, { vsiGreen } from "../../../utils/themeStyles";
import { VsiSelectLabel } from "../../../styled/VsiLabeledSelect.styled";
import { snakeCaseToTitleCase } from "../../../utils/Helpers";

const determineColor = value => {
  switch (value) {
    case 1:
    case "1":
      return "#e10000";
    case -1:
    case "-1":
      return vsiGreen;
    default:
      return "grey";
  }
};

const storageTrConfig = [
  { field: "num_physical_servers", header: "Physical Servers" },
  { field: "num_devices", header: "Devices" },
  { field: "num_pools", header: "Pools" },
  { field: "num_volumes", header: "Volumes" },
  { field: "num_shares", header: "Shares" }
];

const virtualTrConfig = [
  { field: "num_vcenters", header: "vCenters" },
  { field: "num_virtual_data_centers", header: "Data Centers" },
  { field: "num_esx_hosts", header: "ESX Hosts" },
  { field: "num_vms", header: "Virtual Machines (VMs)" },
  { field: "num_datastores", header: "Datastores" },
  { field: "num_cloud_resource_groups", header: "Cloud Resource Groups" },
  { field: "num_cloud_resources", header: "Cloud Resources" },
  { field: "num_databases", header: "Databases" },
  { field: "num_switches", header: "Switches" }
];

export default function InventoryReport({ title, context: reportContext }) {
  useTitleFromComponent(title);

    const { theme, client, periodId } = useVsiAppContext();

  const [params, setParams] = useUrlState({
    context: "storage"
  });

  const { context } = params;

  const [{ recordsets: datacenterData }, { isLoading: isDcDataLoading }] = useDataWithRoute(`/api/v6/report/datacenter_used_growth_proc?client_id=${client.clientId}&period_id=${periodId}`);
  const [storageDatacenters, virtualDatacenters] = datacenterData.length > 1 ? datacenterData : [[], []];

  const [treeData, setTreeData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [displayDataCenters, setDisplayDataCenters] = useState(false);
  const [displayDeviceTypes, setDisplayDeviceTypes] = useState(false);
  const [displayClassifications, setDisplayClassifications] = useState(false);
  const [displayTiers, setDisplayTiers] = useState(false);

  const [{ recordset: executiveData }, { isLoading: isExecLoading }] = useDataWithRoute(`/api/v6/report/business_unit_inventory_proc?client_id=${client.clientId}&period_id=${periodId}`);
  const execObj = executiveData?.[0] ?? {};

  const [{ recordset: buData }, { isLoading: isBuDataLoading }] = useDataWithRoute(`/api/v6/report/business_unit_tagging_list_proc?client_id=${client.clientId}&period_id=${periodId}`);

  const [allBuTags, setAllBuTags] = useState({
    dataCenters: [],
    deviceTypes: [],
    classifications: [],
    tiers: []
  });

  const { dataCenters, deviceTypes, classifications, tiers } = allBuTags;

  const dynamicStorageTrConfig = [
    {
      field: "num_storage_data_centers",
      header: "Data Centers",
      isOpen: displayDataCenters,
      setIsOpen: setDisplayDataCenters,
      list: dataCenters
    },
    {
      field: "num_device_types", header: "Device Types",
      isOpen: displayDeviceTypes,
      setIsOpen: setDisplayDeviceTypes,
      list: deviceTypes
    },
    {
      field: "num_classifications", header: "Classifications",
      isOpen: displayClassifications,
      setIsOpen: setDisplayClassifications,
      list: classifications
    },
    {
      field: "num_tiers", header: "Tiers",
      isOpen: displayTiers,
      setIsOpen: setDisplayTiers,
      list: tiers
    }
  ];

  const onContextChange = (value) => {
    setParams({
      context: value
    });
  };

  const formatTreeData = (data, valueField) => {
    let total = 0;
    const allTreeData = data.map((datum, index) => {
      const dc = datum.datacenter_name || datum.data_center || "Unspecified";
      const value = datum[valueField];
      total += value;
      return {
        id: `id_${dc}_${index}`,
        name: dc,
        color: determineColor(datum.growth),
        growth: datum.growth,
        value
      };
    });
    setTotalCount(total);
    setTreeData(allTreeData);
  };

  useEffect(() => {
    if (context === "storage") {
      formatTreeData(storageDatacenters, "used");
    } else if (context === "virtual") {
      formatTreeData(virtualDatacenters, "num_vms");
    }
  }, [context, virtualDatacenters, storageDatacenters]);

  useEffect(() => {
    const classifications = [];
    const dataCenters = [];
    const deviceTypes = [];
    const tiers = [];
    buData.forEach(bu => {
      const { classification, data_center, device_type_description, tier } = bu;
      if (!classifications.includes(classification)) {
        classifications.push(classification);
      }
      if (!dataCenters.includes(data_center)) {
        dataCenters.push(data_center);
      }
      if (!deviceTypes.includes(device_type_description)) {
        deviceTypes.push(device_type_description);
      }
      if (!tiers.includes(tier)) {
        tiers.push(tier);
      }
    });

    setAllBuTags({
      classifications,
      dataCenters,
      deviceTypes,
      tiers
    });
  }, [buData]);

  let chartTitle = "";
  if (context === "storage") {
    chartTitle = `Utilization by Data Center `;
    if (!isDcDataLoading) {
      chartTitle += `(${totalCount.toLocaleString()} GiB)`;
    }
  } else if (context === "virtual") {
    chartTitle = `VM Count by Data Center `;
    if (!isDcDataLoading) {
      chartTitle += `(${totalCount.toLocaleString()})`;
    }
  }

  const LoadingSpinner = () => isExecLoading ? <Spinner size="sm" color="primary" /> : null;

  return (
    <Card className={`report-card p-2 nav-card-${theme}`}>
      <VsiH4>Inventory Report</VsiH4>
      <VsiBubble noAutobot>
        <Container fluid>
          <VsiSelectLabel>Context</VsiSelectLabel>
          <div className="d-flex align-items-start justify-content-between">
            <ButtonGroup>
              <Button onClick={() => onContextChange("storage")}
                      disabled={context === "storage"}
                      active={context === "storage"}>
                Storage
              </Button>
              <Button onClick={() => onContextChange("virtual")}
                      disabled={context === "virtual"}
                      active={context === "virtual"}>
                Virtual
              </Button>
            </ButtonGroup>
          </div>
        </Container>
        <TreeMap series={[{
          tooltip: {
            valueSuffix: context === "virtual" ? " VMs" : " GiB"
          },
          levels: [{
            level: 1,
            borderWidth: 5,
            dataLabels: {
              enabled: true,
              align: "left",
              verticalAlign: "top",
              color: themeStyles?.[theme]?.textColor,
              style: {
                color: "#FFFFFF",
                fontWeight: "bold",
                fontSize: "1.3rem"
              },
              useHTML: true,
              formatter: function() {
                let arrow = "";
                if (this.point.growth === 1) {
                  arrow = "fa-arrow-trend-up";
                } else if (this.point.growth === -1) {
                  arrow = "fa-arrow-trend-down";
                }
                return `<span>
                          <span>${this.point.name}</span>
                          <i class="fa-regular ${arrow}"></i>
                        </span>`;
              }
            }
          }],
          data: treeData
        }]}
        title={chartTitle} />
      </VsiBubble>
      <Row>
        <Col xs={12} sm={6}>
          <VsiH6 className={"mt-1"}>Storage Overview <LoadingSpinner /></VsiH6>
          <VsiBubble noAutobot>
            <Table striped dark={theme === "dark"}>
              <tbody>
              {storageTrConfig.map(({ field, header }, index) => {
                return (
                  <tr className={`table-${theme}`} key={`tr-${field}-${index}-storage`}>
                    <th>
                      <VsiSpanText>{header}</VsiSpanText>
                    </th>
                    <td style={{ textAlign: "right" }}>
                      {(execObj[field] ?? "").toLocaleString()}
                    </td>
                  </tr>
                );
              })}
              {dynamicStorageTrConfig.map(
                ({
                   header,
                   field,
                   isOpen,
                   setIsOpen,
                   list
                 }, index) => {
                  return (
                    <>
                      <tr key={`tr-${field}-${index}-storage-dynamic`}>
                        <th>
                          <VsiSpanText style={{ cursor: "pointer" }}
                                       onClick={() => setIsOpen(!isOpen)}>
                            {isBuDataLoading ?
                              <Spinner size={"sm"} color={"primary"} />
                              : isOpen ?
                              <i className="fa-solid fa-minus pe-2" />
                                :
                              <i className="fa-solid fa-plus pe-2" />}
                          </VsiSpanText>
                          <VsiSpanText>
                            {header}
                          </VsiSpanText>
                        </th>
                        <td style={{ textAlign: "right" }}>
                          {isBuDataLoading ? '' : list.length}
                        </td>
                      </tr>
                      {list?.map?.((item, itemIndex) => {
                        return (
                          <tr className={`table-${theme} ${isOpen ? "" : "d-none"}`}
                              key={`${item}_${index}_${itemIndex}_num_arrays`}>
                            <td style={{ paddingLeft: 50 }}>{item || "Unspecified"}</td>
                            <td style={{textAlign: 'right'}}/>
                          </tr>
                        );
                      })}
                    </>
                  );
                })}
              </tbody>
            </Table>
          </VsiBubble>
        </Col>
        <Col xs={12} sm={6}>
          <VsiH6 className={"mt-1"}>Virtualization Overview <LoadingSpinner /></VsiH6>
          <VsiBubble noAutobot>
            <Table striped dark={theme === "dark"}>
              <tbody>
              {virtualTrConfig.map(({ field, header }, index) => {
                return (
                  <tr className={`table-${theme}`} key={`tr-${field}-${index}-virtual`}>
                    <th>
                      <VsiSpanText>{header}</VsiSpanText>
                    </th>
                    <td style={{ textAlign: "right" }}>
                      {(execObj[field] ?? "").toLocaleString()}
                    </td>
                  </tr>
                );
              })}
              </tbody>
            </Table>
          </VsiBubble>
        </Col>
      </Row>
    </Card>
  );
}
